import {
  ACCEPTED_AUDIO_FILE_EXTENSIONS,
  ACCEPTED_IMAGE_FILE_EXTENSIONS,
  ACCEPTED_VIDEO_FILE_EXTENSIONS,
} from '@lib/utils/fileExtensions';
import { AddMediaValidationService } from '@services/AddMediaValidationService';

// Instantiated at module level. This is fine since the validation service is stateless —
// multiple instances behave identically, so there's no benefit to singleton/DI patterns.
// Passing it as a parameter was considered but adds boilerplate for callers with no real
// benefit (no need to mock or inject different implementations for stateless validation).
const validationService = new AddMediaValidationService();

export interface DragDropRejected {
  file: File;
  reason: string;
}

export interface DragDropResult {
  files: File[];
  rejected: DragDropRejected[];
}

/**
 * A file that arrived inside a dropped directory, carrying the directories between the drop and
 * itself — `['Trip', 'Day 1']` for `Trip/Day 1/clip.mp4`. Stamped on the `File` rather than
 * returned beside it because the `File` is the only thing that travels the whole way to the
 * upload, including a retry of a failed one (the same convention `FileWithDuration` uses for its
 * extracted metadata). The browser's own `webkitRelativePath` is read-only and left empty for
 * drag-and-drop, which is why this exists.
 */
export type DroppedFile = File & { relativeDirectory?: string[] };

/** The directories a dropped file sat in below the drop; empty for a file dropped on its own. */
export const getRelativeDirectory = (file: File): string[] =>
  (file as DroppedFile).relativeDirectory ?? [];

const stampRelativeDirectory = (file: File, entry: FileSystemEntry) => {
  // `fullPath` is rooted at the drop: '/Trip/Day 1/clip.mp4' for a file inside a dropped 'Trip'.
  const directories = entry.fullPath.split('/').filter(Boolean).slice(0, -1);
  if (directories.length > 0) (file as DroppedFile).relativeDirectory = directories;
};

/**
 * Generates the accept attribute value for file input elements
 * that matches all accepted video and image file extensions
 */
export const getMediaAcceptAttribute = (): string => {
  const videoExtensions = ACCEPTED_VIDEO_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',');
  const imageExtensions = ACCEPTED_IMAGE_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',');
  const audioExtensions = ACCEPTED_AUDIO_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',');
  return `video/*,image/*,audio/*,${videoExtensions},${imageExtensions},${audioExtensions}`;
};

export const getMediaFilesFromFileEntries = async (
  fileEntries: FileSystemEntry[],
): Promise<DragDropResult> => {
  const files: File[] = [];
  const rejected: DragDropRejected[] = [];

  for (let i = 0; i < fileEntries.length; i++) {
    const entry = fileEntries[i];

    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry;
      const file = await fileAsync(fileEntry);
      stampRelativeDirectory(file, entry);
      const validationResult = validationService.fileUpload.isAcceptedMediaFile(file);
      if (validationResult.accepted) {
        files.push(file);
      } else {
        rejected.push({
          file,
          reason: validationResult.reason || 'Unsupported file type',
        });
      }
    } else if (entry.isDirectory) {
      const folderEntry = entry as FileSystemDirectoryEntry;
      const sub = await traverseFolder(folderEntry);
      files.push(...sub.files);
      rejected.push(...sub.rejected);
    }
  }

  return { files, rejected };
};

const readEntriesAsync = (reader: FileSystemDirectoryReader) => {
  return new Promise<FileSystemEntry[]>((resolve, reject) => {
    const entries: FileSystemEntry[] = [];

    // Chrome's version of readEntries is paginated. (This is not the case for Firefox and Safari)
    // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryReader/readEntries#browser_compatibility
    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (batch.length === 0) {
            resolve(entries);
          } else {
            entries.push(...batch);
            readBatch(); // Read next batch
          }
        },
        (error) => {
          reject(error);
        },
      );
    };

    readBatch();
  });
};

const fileAsync = (fileEntry: FileSystemFileEntry) => {
  return new Promise<File>((resolve, reject) => {
    fileEntry.file(
      (file) => {
        resolve(file);
      },
      (error) => {
        reject(error);
      },
    );
  });
};

const traverseFolder = async (folderEntry: FileSystemDirectoryEntry): Promise<DragDropResult> => {
  const files: File[] = [];
  const rejected: DragDropRejected[] = [];

  const reader = folderEntry.createReader();
  const entries = await readEntriesAsync(reader);
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];

    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry;
      const file = await fileAsync(fileEntry);
      stampRelativeDirectory(file, entry);
      const validationResult = validationService.fileUpload.isAcceptedMediaFile(file);
      if (validationResult.accepted) {
        files.push(file);
      } else {
        rejected.push({
          file,
          reason: validationResult.reason || 'Unsupported file type',
        });
      }
    } else if (entry.isDirectory) {
      const folderEntry = entry as FileSystemDirectoryEntry;
      const sub = await traverseFolder(folderEntry);
      files.push(...sub.files);
      rejected.push(...sub.rejected);
    }
  }

  return { files, rejected };
};
